import { forwardRef, type TextareaHTMLAttributes } from "react";
import type { FulfillmentResourceMentionCandidate } from "../../types";
import { MentionTextarea, type MentionTarget } from "./MentionTextarea";
import type { buildTicketSkuDefaults } from "../../service/confirmationTicketEntryService";

type Props = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "onChange"> & {
  value: string; contextKey: string; pointSpuId?: string | null; active?: boolean;
  selectedCandidates?: readonly FulfillmentResourceMentionCandidate[];
  getDefaults?: (candidate: FulfillmentResourceMentionCandidate) => ReturnType<typeof buildTicketSkuDefaults>;
  search: (query: string) => Promise<FulfillmentResourceMentionCandidate[]>;
  onValueChange: (text: string, candidate?: FulfillmentResourceMentionCandidate) => void;
};
const roleLabels: Record<string, string> = { student: "学生", teacher: "老师", guide: "导游", driver: "司机", adult: "成人", child: "儿童", 教师: "老师" };
const roleName = (candidate: FulfillmentResourceMentionCandidate) => roleLabels[candidate.travelerRole || ""] || candidate.travelerRole || "";
const candidateKey = (candidate: FulfillmentResourceMentionCandidate) => `${candidate.spuId}:${candidate.skuId || candidate.skuName}:${candidate.travelerRole || ""}`;

/** 门票规格支持 @ 与兼容 #，角色及完整或半截公式不属于名称替换范围。 */
function ticketMentionAt(input: HTMLTextAreaElement, selectedNames: readonly string[]): MentionTarget | null {
  if (input.selectionStart !== input.selectionEnd) return null;
  const text = input.value, caret = input.selectionStart;
  const start = Math.max(...["@", "＠", "#", "＃"].map(marker => text.lastIndexOf(marker, caret - 1)));
  if (start < 0 || (start > 0 && !/[\s，,；;]/.test(text[start - 1]))) return null;
  const tail = text.slice(start + 1);
  const selectedName = selectedNames.filter(name => tail.startsWith(name) && /^[ \t\u3000]+(?:(?:学生|老师|教师|导游|司机|司陪|成人|儿童)\s*)?(?:[0-9０-９]|待填|人数)/.test(tail.slice(name.length))).sort((left, right) => right.length - left.length)[0];
  if (selectedName && caret > start + 1 + selectedName.length) return null;
  const boundary = tail.search(/[\n\r@＠#＃，,；;]/), segment = boundary < 0 ? tail : tail.slice(0, boundary);
  const formulaText = segment.replace(/[！-～]/g, char => String.fromCharCode(char.charCodeAt(0) - 0xfee0));
  const formula = formulaText.search(/\s+(?:(?:学生|老师|教师|导游|司机|司陪|成人|儿童)(?:[\d.\s人次场元×xX*]|待填|人数|单价)*|(?:\d|待填|人数)(?:[\d.\s人次场元×xX*]|待填|单价)*)$/);
  const name = selectedName || (formula < 0 ? segment : segment.slice(0, formula)).trimEnd();
  const end = start + 1 + name.length;
  return caret < start + 1 || caret > end ? null : { start, end, query: text.slice(start + 1, caret).trim() };
}

/** 只选择当前考察点的真实 SKU，并按票种角色替换输入，不带入住宿默认值。 */
export const TicketSkuMentionInput = forwardRef<HTMLTextAreaElement, Props>(function TicketSkuMentionInput({ contextKey, pointSpuId, active = true, selectedCandidates = [], getDefaults, search, ...props }, ref) {
  return <MentionTextarea {...props} ref={ref} contextKey={JSON.stringify([contextKey, pointSpuId])} active={active && Boolean(pointSpuId)} selectedNames={selectedCandidates.flatMap(candidate => candidate.skuName ? [candidate.skuName] : [])}
    hint={pointSpuId ? "输入 @ 选择本点 SKU，自动带入行程人数和可用参考价；缺失项请补齐，旧#写法继续兼容。" : "请先选择考察点，再输入 @ 搜索该点规格。"}
    resourceLabel="门票规格" popupTitle="选择门票规格" listLabel="门票规格候选" getMention={ticketMentionAt} getKey={candidateKey} getName={candidate => candidate.skuName!} getLabel={candidate => [candidate.skuName, roleName(candidate)].filter(Boolean).join(" · ")}
    search={async query => {
      const keys = new Set<string>();
      return (await search(query)).filter(candidate => {
        const key = candidateKey(candidate);
        if (candidate.resourceType !== "考察点" || candidate.level !== "sku" || !candidate.skuName?.trim() || candidate.spuId !== pointSpuId || keys.has(key)) return false;
        keys.add(key); return true;
      });
    }}
    renderCandidate={candidate => {
      const defaults = getDefaults?.(candidate);
      return defaults ? { content: <span className="confirmation-ticket-sku-main"><strong>{candidate.skuName}</strong><span>{defaults.priceLabel}</span></span>, description: <span>{[defaults.role, defaults.priceSourceLabel].filter(Boolean).join(" · ")}</span> } : undefined;
    }}
    replaceMention={(value, mention, candidate) => {
      const prefix = `${value.slice(0, mention.start)}@${candidate.skuName}`;
      let suffix = value.slice(mention.end);
      const role = roleName(candidate), formulaTail = suffix.split(/[\n\r@＠#＃，,；;]/, 1)[0].trim();
      const defaults = getDefaults?.(candidate);
      if (defaults && /^(?:学生|老师|教师|导游|司机|司陪|成人|儿童)?\s*$/.test(formulaTail)) {
        const boundary = suffix.search(/[\n\r@＠#＃，,；;]/);
        const text = `${prefix} ${defaults.formula}${boundary < 0 ? "" : suffix.slice(boundary)}`;
        const caret = prefix.length + 1 + defaults.role.length;
        return { text, caret, selectionEnd: caret + (defaults.formula.slice(defaults.role.length).match(/^\d+(?:\.\d+)?/)?.[0].length || 0) };
      }
      const scene = /^\d+(?:\.\d+)?\s*(?:场|次)?\s*[×xX*]/.test(formulaTail.normalize("NFKC"));
      if (role && !scene) {
        const existingRole = /^([ \t\u3000]*)(学生|老师|教师|导游|司机|司陪|成人|儿童)/;
        suffix = existingRole.test(suffix) ? suffix.replace(existingRole, `$1${role}`) : ` ${role} ${suffix.replace(/^[ \t\u3000]*/, "")}`;
      }
      const separator = /^[ \t]/.test(suffix) ? "" : " ";
      const caret = prefix.length + (suffix.match(/^\s*(?:学生|老师|教师|导游|司机|司陪|成人|儿童)\s*/)?.[0].length || 1);
      return { text: `${prefix}${separator}${suffix}`, caret, selectionEnd: caret };
    }} />;
});
